import type { Node as ProsemirrorNode } from 'prosemirror-model';
import type { EditorView, NodeView } from 'prosemirror-view';
import type { Observable } from 'rxjs';
import { EMPTY, fromEvent, Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';

import type { ChecklistEvents } from '../events';
import { ChecklistPluginChecklistChange } from '../events';
import { ChecklistItemNode, createCheckListNode, updateChecklistItem } from './checklist-item.node';

export class ChecklistItemNodeView implements NodeView {
  public contentDOM: HTMLElement;
  public dom: HTMLElement;
  public checkbox: HTMLElement;

  private destroy$ = new Subject<void>();

  constructor(
    private node: ProsemirrorNode,
    private view: EditorView,
    private getPos: () => number,
    private canEdit$: Observable<boolean>,
    private listEvents$: Subject<ChecklistEvents>,
  ) {
    const { dom, contentDOM, checkbox } = createCheckListNode(document, this.node);
    this.dom = dom;
    this.contentDOM = contentDOM;
    this.checkbox = checkbox;
    this.addEventListeners();
  }

  public destroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public update(node: ProsemirrorNode) {
    if (ChecklistItemNode.is(node)) {
      this.node = node;
      updateChecklistItem(this.dom, this.checkbox, node.attrs.checked);
      return true;
    }

    return false;
  }

  private addEventListeners() {
    fromEvent(this.checkbox, 'mousedown')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => event.preventDefault());

    const toggleOnClick$ = fromEvent(this.checkbox, 'click').pipe(
      tap(event => {
        event.preventDefault();
        const pos = this.getPos();
        const {
          dispatch,
          state: { tr },
        } = this.view;
        tr.setNodeMarkup(pos, undefined, { checked: !this.node.attrs.checked });
        dispatch(tr);
        this.listEvents$.next(
          new ChecklistPluginChecklistChange(<{ doc: object }>this.view.state.toJSON()),
        );
      }),
    );

    this.canEdit$
      .pipe(
        tap(canEdit => (this.checkbox.style.cursor = canEdit ? 'pointer' : '')),
        switchMap(canEdit => (canEdit ? toggleOnClick$ : EMPTY)),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }
}

export type CreateChecklistItemNodeView = (
  ...args: ConstructorParameters<typeof ChecklistItemNodeView>
) => NodeView;
